// Entries, the API keys external sites post them with, and the scan sessions and
// submissions behind QR result entry — everything that is about someone taking
// part rather than about the tournament itself. getIdToken lives here too,
// because proving who you are to a Cloud Function is what all three need.
//
// Moved here from firebase-auth.js unchanged. It imports cloud-sync.js for the
// tournament each entry belongs to; the dependency runs one way only.
import {
    auth, db,
    doc, setDoc, addDoc, collection, query, where, getDocs, deleteDoc,
    onSnapshot, updateDoc, runTransaction
} from "./firebase-core.js";
import { currentUser } from "./session.js";
import { classifyTournamentStatus, getTournamentById } from "./cloud-sync.js";
import { osloDate } from "../utils/oslo-date.js";

// --- Online Registration Functions ---

/*
 * Creating a registration does NOT happen here.
 *
 * It used to: this module wrote straight into `registrations` and sent the
 * arbiters' notifications itself. Every registration now goes through the public
 * Registration API instead (js/app.js -> submitRegistrationForm, and the same
 * endpoint every external form uses), because the checks that decide whether an
 * entry may be accepted — the duplicate rule, the access code, the rate limits,
 * the immediate import onto the player list — can only be enforced server-side.
 * A browser cannot be trusted to apply them to itself.
 *
 * The functions below still read registrations directly, as far as the security
 * rules allow: an arbiter their tournament's, an account its own. Cancelling goes
 * through the API too (see below).
 */

// A Firebase ID token for the signed-in user, or null for a guest. Sent as
// `Authorization: Bearer <token>` when registering, so the entry is tied to the
// account and the app can offer to cancel it later.
async function getIdToken() {
    if (!auth.currentUser) return null;
    try {
        return await auth.currentUser.getIdToken();
    } catch (e) {
        console.error("Could not get ID token:", e);
        return null;
    }
}

// Real-time listener for an arbiter: receives the full registrations list for a tournament.
// The callback receives the entries AND whether they came only from the local
// cache. The caller needs the second half: with persistentLocalCache in front of
// every query, the first snapshot of a subscription is answered out of IndexedDB
// and holds only the registrations this device has already seen — which for a
// school tournament, whose entries arrive on the booking site and are seated by
// the server between two openings of the app, is a fraction of them. Read as the
// complete live list it deletes players (see js/utils/registration-prune-guard.js).
function listenToRegistrations(tournamentId, callback) {
    const q = query(collection(db, "registrations"), where("tournamentId", "==", tournamentId));
    return onSnapshot(q, (snapshot) => {
        const list = [];
        snapshot.forEach(d => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        callback(list, { fromCache: snapshot.metadata.fromCache === true });
    });
}

// One-shot fetch of registrations for a tournament (requires auth — used by arbiters/logged-in users).
async function getRegistrationsForTournament(tournamentId) {
    try {
        const q = query(collection(db, "registrations"), where("tournamentId", "==", tournamentId));
        const snap = await getDocs(q);
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        return list;
    } catch (e) {
        console.error("Error fetching registrations:", e);
        return [];
    }
}

// Fetch the signed-in user's OWN registration for a tournament (or null).
// Pins registrantUid to the requester so the query is allowed under the
// locked-down registrations rules (non-arbiter spectators may not list a
// whole tournament's registrations). Equality-only compound query — no
// composite index needed.
async function getMyRegistrationForTournament(tournamentId) {
    if (!currentUser) return null;
    try {
        const q = query(
            collection(db, "registrations"),
            where("tournamentId", "==", tournamentId),
            where("registrantUid", "==", currentUser.uid)
        );
        const snap = await getDocs(q);
        if (snap.empty) return null;
        const d = snap.docs[0];
        return { id: d.id, ...d.data() };
    } catch (e) {
        console.error("Error fetching own registration:", e);
        return null;
    }
}

/**
 * Every entry this account owns, paired with the tournament it is for — the
 * data behind the player's My Tournaments tab.
 *
 * WHY REGISTRATIONS AND NOT PLAYER LISTS. What a player has taken part in is
 * really recorded on each tournament's player list, but those live in per-group
 * content documents under tournaments a player has no reason to be able to
 * enumerate. Finding themselves there would mean reading every tournament in
 * the system. The registrations collection is the one place a player's own
 * participation is indexed BY them (`registrantUid`), and the security rules
 * grant exactly that query and no wider one.
 *
 * The consequence is a real limit, not a bug to fix later: a player the arbiter
 * typed straight onto the list never produced a registration, so it cannot
 * appear here. Only online registrations do — including ones made as a guest
 * and since claimed (see the account-link function in functions/index.js).
 *
 * Each result is { ...registration, tournament } where `tournament` is the meta
 * doc plus a computed `status`, or null when the tournament has been deleted or
 * made private since (the entry is still returned — the player did register,
 * and silently dropping it would be more confusing than showing it unresolved).
 */
async function getMyRegistrations() {
    if (!currentUser) return [];
    let regs = [];
    try {
        const q = query(
            collection(db, "registrations"),
            where("registrantUid", "==", currentUser.uid)
        );
        const snap = await getDocs(q);
        snap.forEach(d => regs.push({ id: d.id, ...d.data() }));
    } catch (e) {
        console.error("Error fetching own registrations:", e);
        return [];
    }

    // One read per DISTINCT tournament: several entries for the same event (a
    // parent registering two children, say) must not multiply the reads.
    const today = osloDate();
    const ids = [...new Set(regs.map(r => r.tournamentId).filter(Boolean))];
    const tournaments = new Map();
    await Promise.all(ids.map(async (id) => {
        const data = await getTournamentById(id);
        if (data) tournaments.set(id, { id, ...data, status: classifyTournamentStatus(data, today) });
    }));

    return regs
        .map(r => ({ ...r, tournament: tournaments.get(r.tournamentId) || null }))
        .sort((a, b) => {
            // Newest event first, falling back to when the entry was made so
            // dateless tournaments still land somewhere stable.
            const ad = (a.tournament && a.tournament.startDate) || a.createdAt || '';
            const bd = (b.tournament && b.tournament.startDate) || b.createdAt || '';
            return bd.localeCompare(ad);
        });
}

// --- Cancelling one's own registration ---
//
// The registrations collection is closed to anonymous clients (firestore.rules): a
// registration holds an address, a phone number and a birth date, and its id used to
// be all it took to read and delete it. Cancelling now goes through the Registration
// API, which checks the cancel token from the registrant's link — or, for a signed-in
// registrant, their own account — and hands back only the name, tournament and group
// the confirm dialog shows (handleUnregister in functions/lib/registration-api-handlers.js).

const UNREGISTER_API = `${location.origin}/api/v1/app/unregister`;

async function unregisterCall(path, id, token) {
    const headers = { 'Content-Type': 'application/json' };
    if (currentUser) {
        try { headers.Authorization = `Bearer ${await currentUser.getIdToken()}`; } catch (e) { /* as a guest */ }
    }
    const res = await fetch(UNREGISTER_API + path, {
        method: 'POST',
        headers,
        body: JSON.stringify({ registrationId: String(id || ''), token: String(token || '') })
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, data };
}

// Whose registration a cancel link points at: { id, givenName, lastName, tournamentId,
// tournamentName, groupName }, or null when it does not exist (already cancelled) or
// the link's token does not open it — the two answer alike, on purpose.
async function getRegistrationById(id, token) {
    try {
        const { status, data } = await unregisterCall('/preview', id, token);
        if (status === 200 && data && data.ok) return data.registration;
        if (status !== 404) console.error('Could not look up the registration:', status, data);
    } catch (e) {
        console.error("Error fetching registration:", e);
    }
    return null;
}

// Cancel a registration — from the link in the registrant's email (with its token) or
// from the app by the signed-in account that made it.
async function cancelRegistration(id, token) {
    const { status, data } = await unregisterCall('', id, token);
    if (status === 200 && data && data.ok) return;
    const message = (data && data.error && data.error.message) || (data && data.message) || `HTTP ${status}`;
    throw new Error(message);
}

async function updateRegistrationStatus(id, status) {
    await updateDoc(doc(db, "registrations", id), { status });
}

// --- Registration API keys ---
//
// Credentials external websites use to post registrations (docs/registration-api.md).
// The key string is `ts_sk_<keyId>_<secret>` (secret) or `ts_pk_<keyId>_<secret>`
// (public); the keyId doubles as the Firestore doc id so the Cloud Function can
// verify a key with a single document read instead of a collection scan.
//
// A SECRET key is stored only as a SHA-256 hash — it is shown to the organizer once,
// at creation, and is unrecoverable afterwards (revoke and create a new one). A PUBLIC
// key is designed to sit in a public web page, so it is stored in the clear; what
// protects it is the origin allow-list on the key, not secrecy.

const API_KEY_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

function randomKeyString(length) {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    let out = '';
    // Rejection-free mapping is unnecessary here: 256 % 62 skews the alphabet by
    // well under a bit, and these strings only need to be unguessable.
    for (let i = 0; i < length; i++) out += API_KEY_ALPHABET[bytes[i] % API_KEY_ALPHABET.length];
    return out;
}

async function sha256Hex(text) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('');
}

// Create a key. Returns { key, record } — `key` is the plaintext, available ONLY here.
async function createApiKey({ type = 'secret', label = '', tournamentId = null, allowedOrigins = [] } = {}) {
    if (!currentUser) throw new Error("Must be logged in to create an API key.");
    if (type !== 'secret' && type !== 'public') throw new Error("Unknown API key type.");

    const keyId = randomKeyString(20);
    const secret = randomKeyString(32);
    const plaintext = `ts_${type === 'secret' ? 'sk' : 'pk'}_${keyId}_${secret}`;

    const record = {
        ownerId: currentUser.uid,
        ownerEmail: (currentUser.email || '').toLowerCase(),
        type,
        label: String(label || '').slice(0, 80),
        tournamentId: tournamentId || null,
        allowedOrigins: Array.isArray(allowedOrigins) ? allowedOrigins.slice(0, 20) : [],
        active: true,
        createdAt: new Date().toISOString(),
        lastUsedAt: null,
        usageCount: 0
    };
    if (type === 'secret') {
        record.hash = await sha256Hex(plaintext);
    } else {
        record.publicKey = plaintext;
    }

    await setDoc(doc(db, "apiKeys", keyId), record);
    return { key: plaintext, record: { id: keyId, ...record } };
}

// All of the signed-in organizer's keys, newest first.
async function listApiKeys() {
    if (!currentUser) return [];
    const q = query(collection(db, "apiKeys"), where("ownerId", "==", currentUser.uid));
    const snap = await getDocs(q);
    const list = [];
    snap.forEach(d => list.push({ id: d.id, ...d.data() }));
    list.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
    return list;
}

// Rename / re-scope / change allowed origins / activate-deactivate. The stored
// credential itself is immutable (enforced by firestore.rules too).
async function updateApiKey(keyId, patch) {
    const allowed = ['label', 'tournamentId', 'allowedOrigins', 'active'];
    const clean = {};
    Object.keys(patch || {}).forEach(k => { if (allowed.includes(k)) clean[k] = patch[k]; });
    if (Object.keys(clean).length === 0) return;
    await updateDoc(doc(db, "apiKeys", keyId), clean);
}

async function deleteApiKey(keyId) {
    await deleteDoc(doc(db, "apiKeys", keyId));
}

// --- Scan Results (QR-based distributed result entry) ---

// Create a new scan session (mirror doc) and return its auto-generated token (the QR capability id).
async function createScanSession(payload) {
    if (!currentUser) throw new Error("Must be logged in to start a scan session.");
    const ref = await addDoc(collection(db, "scanSessions"), {
        ...payload,
        ownerId: currentUser.uid,
        active: true,
        updatedAt: new Date().toISOString()
    });
    return ref.id;
}

// Create/refresh the mirror doc for an existing token (merge; recreates it if it was cleaned up).
async function updateScanSession(token, data) {
    if (!token) return;
    await setDoc(doc(db, "scanSessions", token), {
        ...data,
        updatedAt: new Date().toISOString()
    }, { merge: true });
}

async function deactivateScanSession(token) {
    if (!token) return;
    await setDoc(doc(db, "scanSessions", token), {
        active: false,
        updatedAt: new Date().toISOString()
    }, { merge: true });
}

// Arbiter listener: all result submissions for a given scan token.
function listenToScanSubmissions(token, callback) {
    const q = query(collection(db, "resultSubmissions"), where("token", "==", token));
    return onSnapshot(q, (snapshot) => {
        const list = [];
        snapshot.forEach(d => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        callback(list);
    });
}

// `extra`: other fields to record with the status — `appliedTs` when a result was put
// on its board (markScanSubmission in js/app.js).
async function updateScanSubmissionStatus(id, status, extra = {}) {
    await updateDoc(doc(db, "resultSubmissions", id), { ...extra, status });
}

// Take a pending submission for processing. Every arbiter device with the tournament
// open listens to the same submissions, and a pair request must be acted on by exactly
// one of them — so the pending -> processing step is a transaction, and a device that
// loses the race gets false and leaves it alone.
async function claimScanSubmission(id) {
    const ref = doc(db, "resultSubmissions", id);
    return runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists() || snap.data().status !== 'pending') return false;
        tx.update(ref, { status: 'processing' });
        return true;
    });
}

async function deleteScanSubmission(id) {
    await deleteDoc(doc(db, "resultSubmissions", id));
}

export {
    getIdToken,
    listenToRegistrations,
    getRegistrationsForTournament,
    getMyRegistrationForTournament,
    getMyRegistrations,
    getRegistrationById,
    cancelRegistration,
    updateRegistrationStatus,
    createApiKey,
    listApiKeys,
    updateApiKey,
    deleteApiKey,
    createScanSession,
    updateScanSession,
    deactivateScanSession,
    listenToScanSubmissions,
    updateScanSubmissionStatus,
    claimScanSubmission,
    deleteScanSubmission
};
